import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  SF2_FONT_CATALOG,
  getCachedSf2Bank,
  loadSf2RuntimeBank,
  normalizeSf2Patch,
  type InstrumentSlot,
  type Sf2Patch,
  type Sf2PresetMeta,
} from "../../../../../src/lib/audio";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import { projectStore } from "../../store/projectStore";
import { formatOctaveLabel, MusicalKeyboard } from "../MusicalKeyboard";
import { instrumentPresetLearnPath } from "../../controls/instrumentPresetLeaves";
import { ParamKnob } from "../ParamKnob";

/**
 * `kind: "sf2"` instrument body — font / program / gain + keyboard.
 */
export function Sf2Editor({ slot }: { slot: InstrumentSlot }) {
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const instrument = instruments[slot];
  const patch = instrument?.kind === "sf2" ? normalizeSf2Patch(instrument.patch) : null;
  const panelRef = useRef<HTMLDivElement>(null);
  const [octaveShift, setOctaveShift] = useState(0);
  const [presets, setPresets] = useState<Sf2PresetMeta[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useLayoutEffect(() => {
    getLabActions()?.setInstrumentSlot?.(slot);
    setOctaveShift(0);
  }, [slot]);

  useEffect(() => {
    if (!patch) {
      return;
    }
    let cancelled = false;
    const cached = getCachedSf2Bank(patch.fontId);
    if (cached) {
      setPresets(cached.presets);
      setLoadError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void loadSf2RuntimeBank(patch.fontId)
      .then(bank => {
        if (cancelled) {
          return;
        }
        setPresets(bank.presets);
        setLoadError(null);
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        setLoadError(error instanceof Error ? error.message : String(error));
        setPresets([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [patch?.fontId]);

  if (!patch) {
    return (
      <div className="muted flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-2 text-[12px]">
        Slot is not a SoundFont instrument.
      </div>
    );
  }

  function commit(next: Sf2Patch): void {
    getLabActions()?.updateInstrumentSf2Patch?.(slot, next);
  }

  function patchPartial(partial: Partial<Sf2Patch>): void {
    commit(normalizeSf2Patch({ ...patch!, ...partial }));
  }

  const programOptions = useMemo(() => {
    if (presets.length > 0) {
      return presets;
    }
    return [{ bank: patch.bank, program: patch.program, name: `Program ${patch.program}` }];
  }, [presets, patch.bank, patch.program]);

  const programValue = `${patch.bank}:${patch.program}`;

  return (
    <div ref={panelRef} className="flex min-h-0 flex-1 flex-col overflow-hidden outline-none" tabIndex={0}>
      <div className="flex shrink-0 flex-wrap items-center gap-[0.35rem] border-b border-white/[0.06] bg-lab-panel-bar px-2 py-[0.35rem]">
        <select
          className="min-w-0 grow basis-[8rem] rounded border border-lab-line bg-lab-panel px-1.5 py-1 text-[11px] text-slate-200"
          value={patch.fontId}
          aria-label="SoundFont"
          onChange={event => {
            const fontId = event.target.value;
            const entry = SF2_FONT_CATALOG.find(f => f.id === fontId);
            patchPartial({
              fontId,
              bank: entry?.defaultBank ?? 0,
              program: entry?.defaultProgram ?? 0,
            });
          }}
        >
          {SF2_FONT_CATALOG.map(entry => (
            <option key={entry.id} value={entry.id}>{entry.name}</option>
          ))}
        </select>
        <button
          type="button"
          className="rounded border border-lab-line bg-transparent px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.resetInstrumentSlot?.(slot)}
        >
          Reset
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="px-[0.6rem] py-2">
          {loading && (
            <p className="mb-2 text-[11px] text-lab-muted">Loading SoundFont…</p>
          )}
          {loadError && (
            <p className="mb-2 text-[11px] text-red-300">{loadError}</p>
          )}

          <label className="mb-2 flex items-center gap-2 text-[11px] text-lab-muted">
            <span className="w-14 shrink-0">Preset</span>
            <select
              className="min-w-0 grow rounded border border-lab-line bg-lab-panel px-2 py-1 text-[12px] text-slate-200"
              value={programValue}
              onChange={event => {
                const [bankStr, programStr] = event.target.value.split(":");
                patchPartial({
                  bank: Number(bankStr),
                  program: Number(programStr),
                });
              }}
            >
              {programOptions.map(entry => (
                <option key={`${entry.bank}:${entry.program}`} value={`${entry.bank}:${entry.program}`}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>

          <div className="mb-3 flex flex-wrap gap-3">
            <ParamKnob
              label="Gain"
              value={patch.gain}
              min={0}
              max={1}
              step={0.01}
              defaultValue={0.85}
              learnPath={instrumentPresetLearnPath(slot, "gain")}
              onChange={gain => patchPartial({ gain })}
            />
          </div>
        </div>
      </div>

      <div className="shrink-0 border-t border-white/[0.06] bg-lab-panel-bar px-2 py-2">
        <div className="mb-1 flex items-center justify-between text-[10px] text-lab-muted">
          <span>Audition · {formatOctaveLabel(octaveShift)}</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={60 + octaveShift * 12}
          octaves={2}
          voiceMode="poly"
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
      </div>
    </div>
  );
}
